import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Button, CircularProgress, Typography } from "@mui/material";
import { isEmpty } from "lodash";
import { ReactElement, useEffect, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";

import { TargetingForm, TargetingFormSchema } from "@stw/yup-validators";

import {
  CalculateCampaignContactCountMutation,
  CalculateCampaignContactCountMutationVariables,
  CampaignContactCountJob,
  ImportCampaignContactsFromSubscribersMutation,
  ImportCampaignContactsFromSubscribersMutationVariables,
  TrackedJobStatus,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  convertTargetingFormToGraphQL,
  DEFAULT_TARGET,
  TargetGroups,
} from "~frontend/components/audienceFilteringForm";
import {
  CALCULATE_CAMPAIGN_CONTACT_COUNT_JOB,
  GET_CAMPAIGN,
  SUBSCRIBER_CAMPAIGN_CONTACT_IMPORT,
} from "~frontend/graphql/campaigns";
import {
  SubaccountAdminConfigContextValue,
  useSubaccountAdminConfig,
} from "~frontend/providers/SubaccountProvider";
import theme from "~frontend/theme";
import { plural } from "~frontend/util/plural";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../../types";

function ContactCountJobResult({
  job,
}: {
  job: Pick<CampaignContactCountJob, "status" | "result" | "errorMessage">;
}): ReactElement {
  if (
    [
      TrackedJobStatus.Running,
      TrackedJobStatus.Queued,
      TrackedJobStatus.Created,
    ].includes(job.status)
  ) {
    return (
      <Typography
        variant="body2"
        sx={{ marginBottom: theme.spacing(2.5) }}
        component={"div"}
      >
        <FlexBox gap={1} alignItems="center">
          <div>Calculating...</div>
          <CircularProgress
            color="inherit"
            size={12}
            sx={{ marginTop: "-1px" }}
          />
        </FlexBox>
      </Typography>
    );
  }

  if (job.result) {
    return (
      <Typography
        variant="body2"
        sx={{ marginBottom: theme.spacing(2.5) }}
        component={"div"}
      >
        <FlexBox gap={1}>
          <strong>
            {plural(job.result.contactCount, "contact", "contacts")} in this
            campaign
          </strong>
          <span>
            Calculated {new Date(job.result.calculatedAt).toLocaleString()}
          </span>
        </FlexBox>
      </Typography>
    );
  }

  if (job.errorMessage) {
    return (
      <Alert severity="error" sx={{ marginBottom: theme.spacing(2.5) }}>
        {job.errorMessage}
      </Alert>
    );
  }

  return <></>;
}

export default function SubscriberTargeting({
  campaign,
  previousJobFilters,
  readOnly,
}: {
  campaign: CampaignForEditor;
  previousJobFilters: TargetingForm | null;
  readOnly?: boolean;
}): ReactElement {
  const subaccountAdminConfig: SubaccountAdminConfigContextValue =
    useSubaccountAdminConfig()!;

  const audienceId = subaccountAdminConfig.subscriberAudience?.id;

  if (!audienceId) {
    throw new Error("Subscriber audience not found");
  }

  const timezone =
    campaign.textingHoursTimezone || subaccountAdminConfig.textingHoursTimezone;

  const [filterSubscribers, setFilterSubscribers] =
    useState<boolean>(!!previousJobFilters);

  const [startImport] = useMutation<
    ImportCampaignContactsFromSubscribersMutation,
    ImportCampaignContactsFromSubscribersMutationVariables
  >(SUBSCRIBER_CAMPAIGN_CONTACT_IMPORT, {
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
    onErrorSnackbarMessage: "Failed to start subscriber import",
  });

  const [calculateCampaignContactCount] = useMutation<
    CalculateCampaignContactCountMutation,
    CalculateCampaignContactCountMutationVariables
  >(CALCULATE_CAMPAIGN_CONTACT_COUNT_JOB, {
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
    onErrorSnackbarMessage: "Failed to calculate contact count",
    refetchQueries: [GET_CAMPAIGN],
    awaitRefetchQueries: true,
  });

  const defaultTargetGroup = previousJobFilters
    ? TargetingFormSchema.parse(previousJobFilters)
    : { targetGroups: [] };

  const formMethods = useForm<TargetingForm>({
    resolver: zodResolver(TargetingFormSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    values: defaultTargetGroup,
  });

  const { control, handleSubmit, formState } = formMethods;

  async function triggerImport(filters: TargetingForm) {
    return startImport({
      variables: {
        input: {
          campaignId: campaign.id,
          filters: convertTargetingFormToGraphQL(filters),
        },
      },
    });
  }

  async function triggerContactCount(filters: TargetingForm) {
    return calculateCampaignContactCount({
      variables: {
        input: {
          campaignId: campaign.id,
          filters: convertTargetingFormToGraphQL(filters),
        },
      },
    });
  }

  const {
    fields: targetGroups,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "targetGroups",
  });

  useEffect(() => {
    if (targetGroups.length === 0) {
      setFilterSubscribers(false);
    }
  }, [targetGroups, setFilterSubscribers]);

  return (
    <FormProvider {...formMethods}>
      <form name="subscriberFilterForm">
        {filterSubscribers ? (
          <>
            <Typography variant="body1" sx={{ marginBottom: theme.spacing(2) }}>
              Import subscribers who meet the criteria for one or more of the
              following filter groups:
            </Typography>

            <TargetGroups
              control={control}
              audienceId={audienceId}
              timezone={timezone}
              targetGroups={targetGroups}
              removeGroup={remove}
              savedSearchesEnabled={true}
            />

            {campaign.contactCountJob && (
              <ContactCountJobResult job={campaign.contactCountJob} />
            )}

            <FlexBox justifyContent="space-between">
              <Button
                color="tertiary"
                variant="contained"
                sx={{ fontSize: theme.spacing(1.5) }}
                onClick={() =>
                  append(
                    DEFAULT_TARGET as unknown as TargetingForm["targetGroups"][0]
                  )
                }
              >
                + Add Filter Group
              </Button>
              <FlexBox direction="row" gap={1}>
                <Button
                  color="tertiary"
                  variant="contained"
                  onClick={handleSubmit(triggerContactCount)}
                  sx={{ fontSize: theme.spacing(1.5) }}
                  disabled={
                    campaign.contactCountJob?.status ===
                      TrackedJobStatus.Running ||
                    !formState.isValid ||
                    !formState.isDirty
                  }
                >
                  {campaign.contactCountJob
                    ? "Refresh Contact Total"
                    : "Preview Contact Total"}
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleSubmit(triggerImport)}
                  disabled={
                    readOnly ||
                    !isEmpty(formState.errors) ||
                    !formState.isDirty ||
                    !formState.isValid
                  }
                  sx={{ fontSize: theme.spacing(1.5) }}
                  data-testid="import-subscribers-button"
                >
                  Import {targetGroups.length > 0 ? "Filtered " : ""}
                  Subscribers
                </Button>
              </FlexBox>
            </FlexBox>
          </>
        ) : (
          <FlexBox justifyContent="flex-end" gap={1}>
            <Button
              color="tertiary"
              variant="contained"
              sx={{ fontSize: theme.spacing(1.5) }}
              onClick={() => {
                append(
                  DEFAULT_TARGET as unknown as TargetingForm["targetGroups"][0]
                );
                setFilterSubscribers(true);
              }}
            >
              Filter Subscribers
            </Button>

            <Button
              color="primary"
              variant="contained"
              onClick={handleSubmit(triggerImport)}
              disabled={readOnly}
              sx={{ fontSize: theme.spacing(1.5) }}
              data-testid="import-subscribers-button"
            >
              Import All Subscribers
            </Button>
          </FlexBox>
        )}
      </form>
    </FormProvider>
  );
}
